import { exactInteger } from '@/lib/exact'
import type { DecideReason, FormBlock, RowColumn } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import { rowsFrom, submitWrite, writeRefusal } from './writes'

/**
 * Publishing a price list, withdrawing one from sale, and putting it back.
 *
 * # There is no edit control here and there cannot be one
 *
 * A published version is immutable by trigger and a fee event cites it, so
 * "edit" is not an operation this system has. Creating a schedule and changing
 * one are the SAME call — `POST /price-lists` — and that is deliberate on the
 * backend's part: a route that implied an in-place change would be a route
 * describing a change to a row a fee event points at.
 *
 * That is law 6 stated by the domain rather than by the console, which is why
 * every control here is `versioned: true` and labelled **Save as new version**.
 *
 * # The console does not check the PSD2 notice period
 *
 * A publication that raises a fee may not take effect before the configured
 * notice period. That is enforced by a DEFERRED CONSTRAINT TRIGGER, at COMMIT,
 * in the database — and the backend's own handler performs no comparison,
 * because "a check here would be one the next caller could bypass and one that
 * could disagree with the schema".
 *
 * The same argument applies one layer further out, harder. A console that
 * greyed out submit on its own reading of the notice period would be a THIRD
 * implementation of the rule, running on the least reliable copy of the
 * configuration, and it would be the one an operator saw. So these controls
 * state that the rule exists, submit, and render the refusal.
 *
 * It will look like a missing feature. It is the difference between a console
 * that explains a rule and a console that keeps a second opinion about one.
 *
 * # Nothing here charges anybody
 *
 * A price list says what a fee WOULD be. No control in this module writes a
 * posting, reaches the institution or records a fee event — collection is a
 * separate seam because law 2 makes it one.
 */

/** `price_list.publish` — publish, archive and reinstate. Finance. */
const PERM_PUBLISH = 'price_list.publish'

const ROLE = 'Finance'

export const PRICE_LISTS_WRITE_PATH = '/price-lists'

/**
 * The priced lines of a version, as columns.
 *
 * # Every line carries its own currency, and it is required
 *
 * The backend refuses an item that omits one: "a body that omitted it would be
 * priced in whatever the server assumed, and EUR-only is a configuration state
 * rather than a schema one." Law 8 says the same from this side. So `currency`
 * is a required column rather than a form-level field — two lines may price in
 * two currencies, and a single form-level currency would quietly make that
 * impossible while looking like a simplification.
 *
 * # The floor and the cap are nullable and the flat amount is not
 *
 * `min_fee_minor` and `max_fee_minor` are pointers on the wire so that "no cap"
 * and "a cap of zero" are different requests. `flat_minor` is a plain int64: a
 * line with no flat component is a line with a flat component of zero, which is
 * a real and different thing from a line with no ceiling.
 */
const ITEM_COLUMNS: readonly RowColumn[] = [
  {
    name: 'kind', label: 'Kind', kind: 'select', required: true, width: '1 1 190px',
    options: FEE_KINDS(),
  },
  {
    name: 'method', label: 'Method', kind: 'text', required: false, width: '1 1 130px',
    placeholder: 'sepa_ct, card…',
  },
  {
    name: 'currency', label: 'Currency', kind: 'select', required: true, width: '0 0 110px',
    options: [['EUR', 'EUR'], ['USD', 'USD'], ['GBP', 'GBP'], ['CHF', 'CHF'], ['JPY', 'JPY']],
  },
  {
    name: 'flat_minor', label: 'Flat (minor units)', kind: 'amount', required: true,
    width: '1 1 140px', currencyFrom: 'currency', placeholder: '0',
  },
  {
    name: 'percent_bps', label: 'Rate (bps)', kind: 'text', required: false,
    width: '0 0 110px', placeholder: '0',
  },
  {
    name: 'min_fee_minor', label: 'Floor', kind: 'amount', required: false,
    width: '1 1 130px', currencyFrom: 'currency', nullWhenBlank: true,
  },
  {
    name: 'max_fee_minor', label: 'Cap', kind: 'amount', required: false,
    width: '1 1 130px', currencyFrom: 'currency', nullWhenBlank: true,
  },
]

/**
 * The ten fee kinds, from `internal/pricing`.
 *
 * A function rather than a constant so the list is built once at module load
 * and cannot be mutated by a caller holding the array.
 */
function FEE_KINDS(): readonly (readonly [string, string])[] {
  return [
    ['subscription_monthly', 'subscription_monthly — the recurring plan fee'],
    ['incoming_payment', 'incoming_payment — money arriving'],
    ['outgoing_payment', 'outgoing_payment — money leaving'],
    ['internal_transfer', 'internal_transfer — between wallets here'],
    ['fan_payment', 'fan_payment — a supporter paying a creator'],
    ['storefront_discount', 'storefront_discount — a negative line'],
    ['inactivity_monthly', 'inactivity_monthly — the dormancy charge'],
    ['statement_generation', 'statement_generation — issuing a statement'],
    ['atm_withdrawal', 'atm_withdrawal — cash out at a machine'],
    ['fx_markup', 'fx_markup — the spread on a conversion'],
  ]
}

const PUBLISH_REASONS: readonly DecideReason[] = [
  ['new_schedule', 'A schedule that did not exist before', ''],
  ['price_change', 'A deliberate change to what is charged', ''],
  ['regulatory_change', 'Required by a rule or a regulator', ''],
  ['commercial_decision', 'A pricing decision taken commercially', ''],
  ['correction', 'The live version prices something wrongly', ''],
  ['currency_added', 'The same schedule, priced in another currency', ''],
]

const ARCHIVE_REASONS: readonly DecideReason[] = [
  ['withdrawn_from_sale', 'No longer offered to new customers', ''],
  ['superseded', 'A newer schedule replaces it', ''],
  ['commercial_decision', 'Withdrawn commercially', ''],
  ['regulatory_change', 'Required by a rule or a regulator', ''],
]

const REINSTATE_REASONS: readonly DecideReason[] = [
  ['returned_to_sale', 'Offered again', ''],
  ['archived_in_error', 'It should not have been withdrawn', ''],
  ['replacement_withdrawn', 'The schedule that replaced it is gone', ''],
]

/**
 * The sentence every control here carries about the notice period.
 *
 * Repeated verbatim rather than paraphrased per control, because it is a rule
 * an operator either knows or does not, and three wordings of it read as three
 * rules.
 */
const NOTICE =
  'If this raises a fee, the PSD2 notice period applies and the database refuses a boundary that skips it. This console does not check that itself — it would be a third copy of the rule, on the least reliable configuration — so a publication that breaks it is refused on submit with the boundary named.'

/**
 * What a schedule is, as far as a control needs to know.
 *
 * Deliberately not the whole summary. A control needs the code, whether the
 * schedule is currently assignable, and whether it is the default — the three
 * facts that decide which acts are available and what the refusal says.
 */
export interface ScheduleStanding {
  readonly code: string
  readonly displayName: string
  /** `assignable` from the read: false once archived. */
  readonly assignable: boolean
  readonly isDefault: boolean
}

// ---------------------------------------------------------------------------
// Publishing
// ---------------------------------------------------------------------------

/**
 * A schedule that does not exist yet.
 *
 * The same route as publishing a new version — because it is the same
 * operation — but reached from the catalogue, with no baseline to diff against.
 * `versioned` is still true: the label is "Save as new version" because that is
 * what the call does, and a first version is still a version.
 */
export function newScheduleForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_PUBLISH, ROLE, 'Publishing a price list')
  return {
    t: 'form',
    title: 'Publish a new schedule',
    lead: 'Creates a schedule by publishing its first version. Publishing is the only way a price changes here — a published version is immutable and a fee event cites it, so there is no edit and there is no draft that sits unpublished.',
    fields: [
      {
        name: 'code', label: 'Code', kind: 'text', required: true, mono: true,
        note: 'The schedule’s identifier, used in assignments and quoted in fee events. It cannot be changed afterwards; a different code is a different schedule.',
      },
      {
        name: 'display_name', label: 'Display name', kind: 'text', required: true,
        note: 'What operators see. This one can change in a later version.',
      },
      {
        name: 'is_default', label: 'Make this the default schedule', kind: 'checkbox', required: false,
        note: 'The schedule for customers with no assignment. At most one code is the live default and the database enforces it, so ticking this closes the outgoing default at the same boundary. A default schedule can never be withdrawn.',
      },
      {
        name: 'items', label: 'Priced lines', kind: 'rows', required: true,
        columns: ITEM_COLUMNS,
        note: 'Amounts are MINOR UNITS — 150 is €1.50, and 150 JPY is ¥150, because not every currency has two decimal places. Rate is basis points: 25 is 0.25%. An empty floor or cap means there is none; a typed 0 means zero.',
      },
    ],
    reasons: PUBLISH_REASONS,
    reasonNote: 'Recorded against the publication.',
    auditLine: 'admin.price_list.publish · actor · price_list:<code> · reason',
    cta: 'Save as new version',
    ctaNote: NOTICE,
    blocked,
    versioned: true,
    ...(blocked === '' ? { submit: publishSubmit(api) } : {}),
  }
}

/**
 * The next version of a schedule that exists.
 *
 * # The baseline is what makes the diff law 6 requires meaningful
 *
 * The form starts as the live version's own lines, read back through the
 * clone-to-draft endpoint, so an operator edits what is in force rather than
 * retyping it from nothing — and every line they did not touch is visibly
 * unchanged.
 *
 * The clone deliberately omits `code`, `effective_from` and `reason`: those are
 * "the three decisions a clone must not inherit", and asking for them is what
 * makes the publication somebody's decision rather than a copy.
 */
export function nextVersionForm(
  api: ApiClient,
  caller: Caller,
  schedule: ScheduleStanding,
  draft: PriceListDraft | null,
): FormBlock {
  const session = writeRefusal(caller, PERM_PUBLISH, ROLE, 'Publishing a price list version')
  const blocked = session !== ''
    ? session
    : draft === null
      ? 'Disabled — the live version could not be read back into an editable draft, so this console cannot show what a change would change. Publishing without the diff against live would be law 6’s ceremony without its point.'
      : ''

  return {
    t: 'form',
    title: `Publish the next version of ${schedule.code}`,
    lead: 'Starts from the lines in force and publishes what you change as the next version. Nothing is edited in place: the current version stays exactly as it is, and keeps explaining every fee already charged under it.',
    fields: [
      {
        name: 'display_name', label: 'Display name', kind: 'text', required: true,
        value: draft?.displayName ?? schedule.displayName,
      },
      {
        name: 'is_default', label: 'Make this the default schedule', kind: 'checkbox', required: false,
        value: schedule.isDefault ? 'true' : 'false',
        note: 'Carried from the live version. Unticking it here does not choose a replacement — publish the schedule that should take over as default instead.',
      },
      {
        name: 'items', label: 'Priced lines', kind: 'rows', required: true,
        columns: ITEM_COLUMNS,
        value: draft === null ? '' : JSON.stringify(draft.items),
        note: 'The lines in force, ready to change. Removing a line stops it being charged from the new boundary; it does not unwind anything already charged.',
      },
    ],
    reasons: PUBLISH_REASONS,
    reasonNote: 'Recorded against the publication.',
    auditLine: `admin.price_list.publish · actor · price_list:${schedule.code} · reason`,
    cta: 'Save as new version',
    ctaNote: NOTICE,
    blocked,
    versioned: true,
    baseline: baselineOf(draft, schedule),
    ...(blocked === '' ? { submit: publishSubmit(api, schedule.code) } : {}),
  }
}

/**
 * What is live now, for the diff — matched to the fields BY LABEL.
 *
 * The priced lines are rendered as one line of the baseline rather than as
 * seven, because that is the granularity the diff can honestly show: `Form`
 * matches a baseline entry to a field, and `items` is one field. A per-line
 * diff is worth having and is not this; claiming one here would mean rendering
 * a comparison the control did not actually make.
 */
function baselineOf(
  draft: PriceListDraft | null, schedule: ScheduleStanding,
): readonly (readonly [string, string])[] {
  return [
    ['Display name', draft?.displayName ?? schedule.displayName],
    ['Make this the default schedule', schedule.isDefault ? 'true' : 'false'],
    ['Priced lines', draft === null ? '' : JSON.stringify(draft.items)],
  ]
}

function publishSubmit(api: ApiClient, code?: string) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(PRICE_LISTS_WRITE_PATH, {
      code: code ?? (v['code'] ?? ''),
      display_name: v['display_name'] ?? '',
      is_default: v['is_default'] === 'true',
      effective_from: rfc3339(v['effective_from']),
      items: rowsFrom(v['items'], ITEM_COLUMNS).map(withBps),
    }),
    'The version was published',
    (answer) => {
      const version = numberField(answer, 'version')
      const published = stringField(answer, 'code') || code || (v['code'] ?? '—')
      return version === null
        ? [['schedule', published, 'published']]
        : [['schedule', published, ''], ['version', String(version), 'allocated by the store, not chosen here']]
    },
  )
}

/**
 * `percent_bps` as an integer, because the column carries it as text.
 *
 * Basis points and not a percentage, and never a float: 25 is 0.25%. A blank
 * cell is 0 rather than absent — unlike the floor and the cap, the wire field
 * is a plain int and a line with no rate is a line whose rate is zero.
 */
function withBps(
  row: Record<string, string | number | null>,
): Record<string, string | number | null> {
  const raw = row['percent_bps']
  const text = typeof raw === 'string' ? raw.trim() : ''
  return { ...row, percent_bps: /^-?\d+$/.test(text) ? Number(text) : 0 }
}

// ---------------------------------------------------------------------------
// Withdrawing and restoring
// ---------------------------------------------------------------------------

/**
 * Taking a schedule off sale.
 *
 * Not a delete and not a version: the schedule and every version of it stay,
 * and every fee already charged under it stays explained. What changes is that
 * it may not be NEWLY assigned — enforced by a trigger on the assignments
 * table, so it holds against a hand-written INSERT as well as against the
 * route.
 *
 * Customers already on it are not moved. Saying so at the control matters: an
 * operator who reads "withdraw" as "take everyone off" will archive a schedule
 * expecting a migration that does not happen.
 */
export function archiveForm(
  api: ApiClient, caller: Caller, schedules: readonly ScheduleStanding[],
): FormBlock {
  const open = schedules.filter((s) => s.assignable && !s.isDefault)
  const session = writeRefusal(caller, PERM_PUBLISH, ROLE, 'Withdrawing a schedule from sale')
  const blocked = session !== ''
    ? session
    : open.length === 0
      ? 'Disabled — no schedule on this read can be withdrawn. A schedule that is already archived cannot be withdrawn again, and the default schedule can never be withdrawn: customers with no assignment resolve to it, so withdrawing it would leave them priced by nothing.'
      : ''

  return {
    t: 'form',
    title: 'Withdraw a schedule from sale',
    lead: 'Stops a schedule being assigned to anybody new. It deletes nothing, moves nobody, and changes no price: customers already on it stay on it and keep being charged under it until they are moved.',
    fields: [
      {
        name: 'code', label: 'Schedule', kind: 'select', required: true,
        options: open.map((s) => [s.code, `${s.code} — ${s.displayName}`] as const),
        note: 'Only schedules that are on sale and are not the default. The list is what this read returned.',
      },
      {
        name: 'effective_from', label: 'Withdrawn from', kind: 'date', required: true,
        note: 'When it stops being assignable. Never defaulted — this decides which assignments were refusable and when, which is a question somebody asks months later.',
      },
    ],
    reasons: ARCHIVE_REASONS,
    reasonNote: 'Recorded on the archival, and shown beside the schedule from then on.',
    auditLine: 'admin.price_list.archive · actor · price_list:<code> · reason',
    cta: 'Withdraw from sale',
    ctaNote: 'Reversible: a withdrawn schedule can be put back, and the archival stays on the record either way.',
    blocked,
    ...(blocked === '' ? { submit: archiveSubmit(api, 'archive', 'The schedule was withdrawn from sale') } : {}),
  }
}

/** Putting one back. The mirror of the above, and equally not a version. */
export function reinstateForm(
  api: ApiClient, caller: Caller, schedules: readonly ScheduleStanding[],
): FormBlock {
  const archived = schedules.filter((s) => !s.assignable)
  const session = writeRefusal(caller, PERM_PUBLISH, ROLE, 'Returning a schedule to sale')
  const blocked = session !== ''
    ? session
    : archived.length === 0
      ? 'Disabled — no schedule on this read is withdrawn, so there is none to put back.'
      : ''

  return {
    t: 'form',
    title: 'Return a schedule to sale',
    lead: 'Makes a withdrawn schedule assignable again. The archival stays on the record with who withdrew it and why — putting a schedule back does not erase that it was taken off.',
    fields: [
      {
        name: 'code', label: 'Schedule', kind: 'select', required: true,
        options: archived.map((s) => [s.code, `${s.code} — ${s.displayName}`] as const),
      },
      {
        name: 'effective_from', label: 'Assignable from', kind: 'date', required: true,
        note: 'When it becomes assignable again. Never defaulted, for the same reason the withdrawal boundary is not.',
      },
    ],
    reasons: REINSTATE_REASONS,
    reasonNote: 'Recorded against the reinstatement, beside the archival it ends.',
    auditLine: 'admin.price_list.reinstate · actor · price_list:<code> · reason',
    cta: 'Return to sale',
    ctaNote: 'Assignments that were refused while it was withdrawn are not retried — nothing here reruns a refusal.',
    blocked,
    ...(blocked === '' ? { submit: archiveSubmit(api, 'reinstate', 'The schedule was returned to sale') } : {}),
  }
}

function archiveSubmit(api: ApiClient, segment: 'archive' | 'reinstate', heading: string) {
  return (v: Readonly<Record<string, string>>) => {
    const code = v['code'] ?? ''
    return submitWrite(
      () => api.post<unknown>(
        `${PRICE_LISTS_WRITE_PATH}/${encodeURIComponent(code)}/${segment}`,
        { effective_from: rfc3339(v['effective_from']), reason: v['reason'] ?? '' },
      ),
      heading,
      () => [[
        'schedule', code === '' ? '—' : code,
        segment === 'archive' ? 'no longer assignable to anybody new' : 'assignable again',
      ]],
    )
  }
}

// ---------------------------------------------------------------------------
// The clone-to-draft read
// ---------------------------------------------------------------------------

/** The live version's lines, in the shape the publish endpoint accepts. */
export interface PriceListDraft {
  readonly displayName: string
  /** Cells as strings, ready to be the `rows` field's initial value. */
  readonly items: readonly Record<string, string>[]
}

export function priceListDraftPath(code: string, version: number): string {
  return `${PRICE_LISTS_WRITE_PATH}/${encodeURIComponent(code)}/versions/${String(version)}/draft`
}

/**
 * The live version, read back as something editable.
 *
 * # Why this is a GET and why it matters that it is
 *
 * A POST that cloned server-side "would publish prices nobody looked at, and a
 * line the operator did not intend to sell would be indistinguishable in the
 * audit log from one somebody chose". So the clone lands in a form, and nothing
 * is published until somebody submits it.
 *
 * # It needs the PUBLISH permission, not the read one
 *
 * Deliberately: this is the authoring surface, and it hands back the shape the
 * publish endpoint accepts rather than the shape a support agent reads. So a
 * session that may read a schedule and not publish one gets `null` here — and
 * the control it feeds is already disabled for the same session, by the same
 * permission, with the role named.
 *
 * # Every failure is `null`
 *
 * There is no partial draft. A form prefilled from half a read would show an
 * operator a diff against a version that does not exist, which is worse than
 * showing them no control at all.
 */
export async function readPriceListDraft(
  api: ApiClient, code: string, version: number,
): Promise<PriceListDraft | null> {
  let payload: unknown
  try {
    payload = await api.get<unknown>(priceListDraftPath(code, version))
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }
  if (typeof payload !== 'object' || payload === null) return null
  const raw = (payload as Record<string, unknown>)['items']
  if (!Array.isArray(raw)) return null

  const items: Record<string, string>[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue
    const it = entry as Record<string, unknown>
    items.push({
      kind: str(it['kind']),
      method: str(it['method']),
      currency: str(it['currency']),
      flat_minor: num(it['flat_minor']),
      percent_bps: num(it['percent_bps']),
      // Absent stays absent. `num` renders null and undefined as '', which is
      // what the editor reads as "no floor" and what `rowsFrom` sends back as
      // `null` — so a version with no cap round-trips without acquiring one.
      min_fee_minor: num(it['min_fee_minor']),
      max_fee_minor: num(it['max_fee_minor']),
    })
  }
  return { displayName: str((payload as Record<string, unknown>)['display_name']), items }
}

// ---------------------------------------------------------------------------

/**
 * A date field's value as the instant the backend parses.
 *
 * `effective_from` is RFC3339 on every route here and is never defaulted. The
 * `date` input gives `YYYY-MM-DD`, so the boundary is midnight UTC of that day
 * — stated rather than assumed, because a boundary that silently meant "now"
 * would make "the operator chose today" and "the operator forgot to say" the
 * same request, which is exactly what the backend refuses to allow.
 *
 * Anything already carrying a time is passed through: a caller supplying a full
 * instant means it.
 */
function rfc3339(day: string | undefined): string {
  const d = (day ?? '').trim()
  if (d === '') return ''
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00Z` : d
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

/** A number as the digits an editor shows. Absent stays absent. */
function num(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(v) : ''
}

function stringField(answer: unknown, key: string): string {
  if (typeof answer !== 'object' || answer === null) return ''
  const v = (answer as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : ''
}

function numberField(answer: unknown, key: string): number | null {
  if (typeof answer !== 'object' || answer === null) return null
  const v = (answer as Record<string, unknown>)[key]
  return exactInteger(v)
}
